<template>
  <v-container fluid class="pt-4">
    <v-row>
      <v-col sm="9" cols="12">
        <v-autocomplete
          v-model="selectedProjects"
          label="Projects"
          :items="projectNames"
          multiple
          dense
          chips
        >
          <template v-slot:selection="{ item, index }">
            <v-chip v-if="index < 4" small color="primary" outlined>
              <span>{{ item }}</span>
            </v-chip>
            <span v-if="index === 4" class="grey--text text-caption">
              (+{{ selectedProjects.length - 4 }} others)
            </span>
          </template>
        </v-autocomplete>
      </v-col>
      <v-col sm="3" cols="12" class="pt-2">
        <v-select v-model="selectedYears" label="Year" :items="yearRange" multiple />
      </v-col>
    </v-row>
    <v-card v-if="projectStorages.length">
      <v-card-title> Storages </v-card-title>
      <v-expansion-panels multiple v-model="storagesPanels">
        <v-expansion-panel v-for="(storage, i) in projectStorages" :key="i">
          <v-expansion-panel-header>
            {{ storage.resourceName }}
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-expansion-panels multiple v-model="storagesDataPanels[i]" class="px-4">
              <v-expansion-panel v-for="(key, index) in storagesDataKey" :key="key" class="px-4">
                <v-expansion-panel-header>
                  {{ camelToTitleCase(key) }}
                </v-expansion-panel-header>
                <v-expansion-panel-content class="pa-4">
                  <Plotly
                    :layout="storagesLayouts[index]"
                    :toImageButtonOptions="
                      chartDownloadName(caseName, 'SSE', storagesLayouts[index].title)
                    "
                    :data="getTotalChartData(storage.resourceId, 'storages', key)"
                  />
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-card>
    <v-card v-if="projectGenerators.length">
      <v-card-title> Generators </v-card-title>
      <v-expansion-panels multiple v-model="generatorsPanels">
        <v-expansion-panel v-for="(generator, i) in projectGenerators" :key="i">
          <v-expansion-panel-header>
            {{ generator.resourceName }}
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-expansion-panels multiple v-model="generatorsDataPanels[i]" class="px-4">
              <v-expansion-panel v-for="key in generatorsDataKey" :key="key" class="px-4">
                <v-expansion-panel-header>
                  {{ camelToTitleCase(key) }}
                </v-expansion-panel-header>
                <v-expansion-panel-content class="pa-4">
                  <v-col sm="3" class="py-0">
                    <v-select
                      :items="projectStorages"
                      v-model="selectedStorageToComp"
                      multiple
                      return-object
                      label="Storage"
                      item-text="resourceName"
                    />
                  </v-col>
                  <v-col v-for="year in selectedYears" :key="year">
                    <Plotly
                      :layout="layoutCreation(year, 'P&L [$]')"
                      :toImageButtonOptions="
                        chartDownloadName(caseName, 'SSE', layoutCreation(year, 'P&L [$]').title)
                      "
                      :data="getResourceCompChartData(year, selectedStorageToComp, generator, key)"
                    />
                  </v-col>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-card>
  </v-container>
</template>

<script>
import { camelToTitleCase, camelCase, chartDownloadName } from "@/plugins/utils";

export default {
  name: "OutputSSEMultipleProject",
  components: {
    Plotly: () => import("@/components/charts/Plotly.vue"),
  },
  mixins: [],
  props: {
    yearlyResults: { type: Object, required: true }, // {project name -> that proj's yearlyResults}
    inputCase: { type: Object, required: true },
    logoUrl: { type: String, required: true },
    yearRange: { type: Array, required: true },
  },
  data() {
    return {
      caseName: this.inputCase.name,
      tabularDataKeys: {
        storages: [
          "discharge",
          "charge",
          "cycles",
          "energyThroughput",
          "purchases",
          "sales",
          "pL",
          "operationHours",
        ],
        generators: ["pL"],
      },
      selectedProjects: Object.keys(this.yearlyResults).slice(0, 20),
      selectedYears: this.yearRange,
      selectedStorageToComp: [],
      storagesDataKey: ["pL", "energyThroughput", "cycles", "operationHours"],
      generatorsDataKey: ["pL"],
      storagesLayouts: [
        {
          title: "P&L",
          images: [this.acelerexLogo],
          yaxis: {
            title: "$",
            fixedrange: true,
          },
          xaxis: {
            dtick: 1,
          },
        },
        {
          title: "Energy Throughput",
          images: [this.acelerexLogo],
          yaxis: {
            title: "MWh",
            fixedrange: true,
          },
          xaxis: {
            dtick: 1,
          },
        },
        {
          title: "Annual Cycles",
          images: [this.acelerexLogo],
          yaxis: {
            title: "Cycle Count",
            fixedrange: true,
          },
          xaxis: {
            dtick: 1,
          },
        },
        {
          title: "Operation Hours",
          images: [this.acelerexLogo],
          yaxis: {
            title: "Hours",
            fixedrange: true,
          },
          xaxis: {
            dtick: 1,
          },
        },
      ],
      storagesPanels: [],
      storagesDataPanels: [],
      generatorsPanels: [],
      generatorsDataPanels: [],
    };
  },
  computed: {
    yearRangebyProject() {
      return this.projectNames.reduce((acc, name) => {
        acc.push(
          ...this.yearRange.map((el) => ({
            project: name,
            year: el,
          }))
        );
        return acc;
      }, []);
    },
    headers() {
      const headers = [
        {
          text: "Parameters",
          value: "name",
        },
      ];
      this.projectNames.forEach((name) =>
        headers.push(
          ...this.yearRange.map((year) => ({
            text: `${name}${year}`,
            value: `${this.camelCase(name)}${year}`,
          }))
        )
      );

      return headers;
    },
    displayDivider() {
      return this.$vuetify.breakpoint.smAndUp;
    },
    projectNames() {
      return Object.keys(this.yearlyResults);
    },
    startYear() {
      return this.inputCase.settings.year_start;
    },
    projectGenerators() {
      return this.getResource("generators");
    },
    projectStorages() {
      return this.getResource("storages");
    },
    SSEAllYearlyOutput() {
      return this.yearlyResults;
    },
    sortedSelectedYear() {
      if (this.selectedYears) {
        const sortedYears = this.selectedYears;
        return sortedYears.sort();
      }
      return [];
    },
  },
  watch: {},
  beforeCreate() {},
  created() {},
  beforeMount() {},
  mounted() {},
  beforeUpdate() {},
  updated() {},
  activated() {},
  deactivated() {},
  beforeDestroy() {},
  destroyed() {},
  methods: {
    chartDownloadName,
    mapServiceToOutputKey(service) {
      return {
        energyArbitrage: "EA",
        peakShavingGrid: "PS",
        blackService: "BS",
        ESOCManagement: "ESOCM",
        marketEnergyArbitrage: "MEA",
        renewableEconomicShifting: "RS",
        demandChargeManagement: "DCM",
      }[service];
    },
    camelCase,
    camelToTitleCase,
    layoutCreation(plotTitle, yaxisTitle) {
      return {
        title: `${yaxisTitle} (${plotTitle})`,
        images: [this.acelerexLogo],
        yaxis: {
          title: yaxisTitle,
          fixedrange: true,
        },
        xaxis: {
          dtick: 1,
        },
      };
    },
    getResourceWithSource(resource) {
      const resourceItem = this.projectNames.reduce((acc, projectName) => {
        if (this.inputCase.input.projects.find((v) => v.name === projectName)[resource]) {
          this.inputCase.input.projects
            .find((v) => v.name === projectName)
            [resource].forEach((el) => {
              acc.push({
                resourceName: el.name,
                resourceId: el.id,
                resourceKey: resource,
                resourceSource: el.source,
              });
            });
        }
        return acc;
      }, []);
      return resourceItem.reduce((acc, current) => {
        const x = acc.find((item) => item.resourceId === current.resourceId);
        if (!x) {
          return acc.concat([current]);
        }
        return acc;
      }, []);
    },
    getResource(resource) {
      const resourceItem = this.projectNames.reduce((acc, projectName) => {
        if (this.SSEAllYearlyOutput[projectName][this.startYear][resource]) {
          Object.values(this.SSEAllYearlyOutput[projectName][this.startYear][resource]).forEach(
            (el) => {
              acc.push({
                resourceName: el.name,
                resourceId: el.id,
                resourceKey: resource,
              });
            }
          );
        }

        return acc;
      }, []);

      const projectResource = resourceItem.reduce((acc, current) => {
        const x = acc.find((item) => item.resourceId === current.resourceId);
        if (!x) {
          return acc.concat([current]);
        }
        return acc;
      }, []);
      // Set <resource>Panels value to open by default
      this[`${resource}DataPanels`] = [];
      this[`${resource}Panels`] = projectResource.map((_v, i) => {
        this[`${resource}DataPanels`].push(this[`${resource}DataKey`].map((_key, j) => j));
        return i;
      });
      return projectResource;
    },
    getResourceCompChartData(year, resource1, resource2, dataKey) {
      if (this.selectedProjects && this.sortedSelectedYear) {
        const selectedResources = [resource2, ...resource1];
        const data = selectedResources.map((resource) => ({
          x: this.selectedProjects,
          y: this.selectedProjects.reduce((acc, projectName) => {
            acc.push(
              Object.values(this.SSEAllYearlyOutput[projectName][year][resource.resourceKey]).find(
                (f) => f.id === resource.resourceId
              )
                ? Object.values(
                    this.SSEAllYearlyOutput[projectName][year][resource.resourceKey]
                  ).find((f) => f.id === resource.resourceId)[dataKey]
                : 0
            );
            return acc;
          }, []),
          name:
            resource.resourceKey === "generators" ? resource.resourceName : resource.resourceName,
          showlegend: true,
          type: "bar",
        }));
        return data;
      }
      return [];
    },
    getTotalChartData(id, resource, key) {
      if (
        this.sortedSelectedYear &&
        this.sortedSelectedYear.length === 1 &&
        this.selectedProjects
      ) {
        const oneYearData = this.selectedProjects.map((projectName) => ({
          project: projectName,
          value: Object.values(
            this.SSEAllYearlyOutput[projectName][this.sortedSelectedYear[0]][resource]
          ).find((f) => f.id === id)
            ? Object.values(
                this.SSEAllYearlyOutput[projectName][this.sortedSelectedYear[0]][resource]
              ).find((f) => f.id === id)[key]
            : 0,
        }));
        const sortedData = oneYearData.sort((a, b) => (a.value > b.value ? 1 : -1));
        const projectNames = sortedData.map((v) => v.project);
        const values = sortedData.map((v) => v.value);
        return [
          {
            x: projectNames,
            y: values,
            name: this.sortedSelectedYear[0],
            showlegend: true,
            type: "bar",
          },
        ];
      }
      if (this.selectedProjects && this.sortedSelectedYear) {
        const data = this.sortedSelectedYear.map((year) => ({
          x: this.selectedProjects,
          y: this.selectedProjects.reduce((acc, projectName) => {
            acc.push(
              Object.values(this.SSEAllYearlyOutput[projectName][year][resource]).find(
                (f) => f.id === id
              )
                ? Object.values(this.SSEAllYearlyOutput[projectName][year][resource]).find(
                    (f) => f.id === id
                  )[key]
                : 0
            );
            return acc;
          }, []),
          name: year,
          type: "bar",
          showlegend: true,
        }));
        if (data.length === 1) {
          data[0].y.sort((a, b) => (a > b ? 1 : -1));
        }
        return data;
      }
      return [];
    },
  },
};
</script>

<style></style>
